import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useAsync } from '../../lib/store';
import type { BreederProfile } from '../../lib/types';
import { Panel } from '../ui';
import { formatNumber } from '../../lib/format';

/**
 * The public breeder profile — design PART 22 and 23.
 *
 * Reputation here is **statistical, never a rating**. Every number is something
 * the trainer actually did, counted from rows that already exist, so the only
 * way to move any of it is to breed — there is nothing to review-bomb and
 * nothing to farm that is not the game itself.
 *
 * Rendered as its own panel and fetched separately from the profile, because a
 * trainer who has never opened the Nursery should cost nothing extra to look
 * at: the panel returns nothing and draws nothing.
 */
export function BreederPanel({ userId }: { userId: string }) {
  const state = useAsync<BreederProfile>(
    () => api.get(`/breeder/profile/${userId}`),
    [userId],
  );

  const data = state.data;
  // No panel at all for somebody who has never bred anything. An empty
  // "Breeder" card on every profile in the game would be chrome, not content.
  if (!data || data.eggsProduced === 0) return null;

  return (
    <Panel title="❦ Breeder" hint={data.primarySpecies?.name ?? undefined}>
      <div
        className="grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))', gap: 8 }}
      >
        <Stat label="Eggs produced" value={formatNumber(data.eggsProduced)} />
        <Stat label="Eggs hatched" value={formatNumber(data.eggsHatched)} />
        <Stat label="Shinies bred" value={formatNumber(data.shiniesBred)} />
        {data.rareBred > 0 && <Stat label="Rare bred" value={formatNumber(data.rareBred)} />}
        {data.highestGeneration > 0 && (
          <Stat label="Highest gen" value={String(data.highestGeneration)} />
        )}
        {data.longestHunt > 0 && <Stat label="Longest hunt" value={formatNumber(data.longestHunt)} />}
      </div>

      {data.bestPair && data.bestPair.eggsProduced > 0 && (
        <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
          Their best pairing has produced {formatNumber(data.bestPair.eggsProduced)} eggs together
          {data.bestPair.shinyOffspring > 0 && `, ${data.bestPair.shinyOffspring} of them shiny`}.
        </p>
      )}

      {data.bloodlines.length > 0 && (
        <>
          <hr className="divider" />
          <div className="stack-sm">
            <div className="tiny muted">Registered lines</div>
            {data.bloodlines.map((line) => (
              <div key={line.id} className="row row--between tiny">
                <strong>{line.name}</strong>
                <span className="muted">
                  {formatNumber(line.totalDescendants)} descendants
                  {line.highestGeneration > 0 && ` · gen ${line.highestGeneration}`}
                </span>
              </div>
            ))}
          </div>
        </>
      )}

      {data.reputation.bazaarSales > 0 && (
        <p className="tiny muted" style={{ marginTop: 'var(--gap-sm)' }}>
          {formatNumber(data.reputation.bazaarSales)} sales through the Bazaar.{' '}
          <Link to={`/bazaar?breeder=${encodeURIComponent(data.username)}`}>
            Find their work
          </Link>
        </p>
      )}
    </Panel>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="stack-sm" style={{ gap: 0 }}>
      <div className="tiny muted">{label}</div>
      <div className="mono">{value}</div>
    </div>
  );
}
